import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Trash2, MessageSquarePlus, Pencil, Eye, Pin, Search, FolderOpen } from "lucide-react";
import { MentionTextarea, MentionText } from "@/components/MentionTextarea";

const stamp = (iso) => {
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return "";
  return dt.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
};

export function ClientNoteLog({ clientId, canManage }) {
  return <NoteLog basePath={`/clients/${clientId}`} canManage={canManage} pinnable testPrefix="" />;
}

export function LeadNoteLog({ leadId }) {
  return <NoteLog basePath={`/leads/${leadId}`} canManage pinnable testPrefix="lead-" saveLabel="Save note" />;
}

/** Case notes log (replaces the old single "Staff Notes" box). Copied to the client's notes when the case closes. */
export function CaseNoteLog({ appId, canManage, onAdded }) {
  return <NoteLog basePath={`/applications/${appId}`} canManage={canManage} testPrefix="case-" title="Case Notes" onAdded={onAdded}
    emptyText="No case notes yet." hint="Each note keeps who wrote it and when. Copied to the client's notes when the case is closed." />;
}

/** Internal note log — each entry keeps its author and the time it was written. */
function NoteLog({ basePath, canManage, pinnable = false, testPrefix = "", saveLabel = "Add note", title, emptyText, hint, onAdded }) {
  const { user } = useAuth();
  const [notes, setNotes] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [policy, setPolicy] = useState("nobody");
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  useEffect(() => { api.get("/settings").then((r) => setPolicy(r.data.note_editing || "nobody")).catch(() => {}); }, []);

  const load = () => api.get(`${basePath}/notes`).then((r) => setNotes(r.data)).catch(() => setNotes([]));
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [basePath]);

  const add = async () => {
    const body = text.trim();
    if (!body) return;
    setBusy(true);
    try {
      const { data } = await api.post(`${basePath}/notes`, { body });
      setNotes((xs) => [data, ...(xs || [])]);
      setText("");
      toast.success("Note logged");
      onAdded?.(data);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const remove = async (n) => {
    if (!window.confirm("Delete this note? This cannot be undone.")) return;
    try {
      await api.delete(`${basePath}/notes/${n.id}`);
      setNotes((xs) => (xs || []).filter((x) => x.id !== n.id));
      toast.success("Note deleted");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const canDelete = (n) => !n.readonly && (policy === "admins" ? user?.role === "admin" : policy === "writer_admins" ? (user?.role === "admin" || n.author_id === user?.id) : false);
  const sq = search.trim().toLowerCase();
  const shown = (notes || []).filter((n) => !sq || `${n.body} ${n.author_name || ""}`.toLowerCase().includes(sq));
  const pinnedCount = (notes || []).filter((n) => n.pinned_at).length;
  const togglePin = async (n) => {
    try {
      await api.post(`${basePath}/notes/${encodeURIComponent(n.id)}/pin`, { pinned: !n.pinned_at });
      load(); toast.success(n.pinned_at ? "Note unpinned" : "Note pinned");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const saveEdit = async () => {
    const body = editing.text.trim();
    if (!body) return;
    try {
      const { data } = await api.put(`${basePath}/notes/${editing.id}`, { body });
      setNotes((xs) => xs.map((x) => (x.id === data.id ? data : x))); setEditing(null); toast.success("Note updated");
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div data-testid={testPrefix === "case-" ? "case-note-log" : "client-note-log"}>
      <label className="text-xs font-medium text-slate-500">{title || (testPrefix === "lead-" ? "Notes" : "Internal Notes")} <span className="text-slate-400 font-normal">· staff only</span></label>
      {hint && <p className="text-[11px] text-slate-400 mt-0.5">{hint}</p>}
      {canManage && (
        <div className="mt-1 space-y-2">
          <MentionTextarea rows={3} value={text} onChange={setText} placeholder="Add a note — type @ to mention a teammate."
            data-testid="internal-notes" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-primary/30" />
          <button onClick={add} disabled={busy || !text.trim()} data-testid="add-internal-note"
            className="inline-flex items-center gap-1.5 bg-secondary text-white px-3 py-2 rounded-lg text-xs font-semibold disabled:opacity-50 hover:brightness-110 transition-[filter]">
            <MessageSquarePlus className="h-3.5 w-3.5" /> {busy ? "Saving…" : saveLabel}
          </button>
        </div>
      )}
      {(notes || []).length > 3 && (
        <div className="mt-3 relative">
          <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search notes…" data-testid={`${testPrefix}notes-search`}
            className="w-full pl-8 pr-3 py-1.5 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-primary/30" />
        </div>
      )}
      <div className="mt-3 space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="internal-notes-list">
        {notes === null ? (
          <div className="text-xs text-slate-400">Loading notes…</div>
        ) : notes.length === 0 ? (
          <div className="text-xs text-slate-400" data-testid="internal-notes-empty">{emptyText || "No internal notes yet."}</div>
        ) : shown.length === 0 ? (
          <div className="text-xs text-slate-400" data-testid="notes-search-empty">No notes match “{search}”.</div>
        ) : [...shown.filter((n) => n.pinned_at).sort((x, y) => (y.pinned_at || "").localeCompare(x.pinned_at || "")), ...shown.filter((n) => !n.pinned_at)].map((n, idx, arr) => (
          <div key={n.id}>
          {n.pinned_at && idx === 0 && <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mb-1 flex items-center gap-1" data-testid="pinned-notes-heading"><Pin className="h-3 w-3" /> Pinned</div>}
          {!n.pinned_at && idx > 0 && arr[idx - 1].pinned_at && <div className="border-t border-dashed border-slate-200 my-2" />}
          <div data-testid={`internal-note-${n.id}`} className={`group rounded-lg border px-3 py-2 ${n.pinned_at ? "border-amber-200 bg-amber-50/70" : "border-slate-100 bg-slate-50/70"}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="text-[11px] font-semibold text-slate-600">{n.author_name || "Staff"}</div>
              <div className="flex items-center gap-1.5 shrink-0">
                <span className="text-[10px] text-slate-400" data-testid={`internal-note-time-${n.id}`}>{stamp(n.created_at)}</span>
                {pinnable && (
                  <button onClick={() => togglePin(n)} data-testid={`pin-note-${n.id}`} disabled={!n.pinned_at && pinnedCount >= 3}
                    title={n.pinned_at ? "Unpin" : pinnedCount >= 3 ? "Unpin a note first (max 3)" : "Pin to top"}
                    className={`transition-opacity disabled:cursor-not-allowed ${n.pinned_at ? "text-amber-600" : "text-slate-300 hover:text-amber-600 opacity-0 group-hover:opacity-100"}`}><Pin className={`h-3.5 w-3.5 ${n.pinned_at ? "fill-amber-500" : ""}`} /></button>
                )}
                {canDelete(n) && (
                  <button onClick={() => setEditing({ id: n.id, text: n.body })} data-testid={`edit-internal-note-${n.id}`} title="Edit note"
                    className="text-slate-300 hover:text-primary opacity-0 group-hover:opacity-100 transition-opacity"><Pencil className="h-3.5 w-3.5" /></button>
                )}
                {canDelete(n) && (
                  <button onClick={() => remove(n)} data-testid={`delete-internal-note-${n.id}`} title="Delete note"
                    className="text-slate-300 hover:text-rose-500 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="h-3.5 w-3.5" /></button>
                )}
              </div>
            </div>
            {n.kind === "reassignment" && <div className="mt-0.5 mr-1 inline-flex items-center gap-1 text-[10px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 px-1.5 py-0.5 rounded-full" data-testid={`note-reassignment-${n.id}`}>Reassigned</div>}
            {n.kind === "closing_summary" && <div className="mt-0.5 mr-1 inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full" data-testid={`note-closing-summary-${n.id}`}>Closing summary</div>}
            {n.source_case_label && <div className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-semibold text-sky-700 bg-sky-50 border border-sky-200 px-1.5 py-0.5 rounded-full" data-testid={`note-case-tag-${n.id}`}><FolderOpen className="h-3 w-3" /> From case {n.source_case_label}</div>}
            {n.legacy_client_visible && <div className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full" data-testid={`note-legacy-${n.id}`}><Eye className="h-3 w-3" /> Previously visible to client</div>}
            {editing?.id === n.id ? (
              <div className="mt-1 space-y-1.5">
                <textarea rows={3} value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} data-testid={`edit-note-text-${n.id}`} className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm" />
                <div className="flex gap-2"><button onClick={saveEdit} data-testid={`edit-note-save-${n.id}`} className="px-3 py-1 rounded-lg bg-primary text-white text-xs font-semibold">Save</button><button onClick={() => setEditing(null)} className="px-3 py-1 rounded-lg border border-slate-200 text-xs font-semibold">Cancel</button></div>
              </div>
            ) : <div className="text-sm text-slate-700 whitespace-pre-wrap mt-0.5"><MentionText text={n.body} /></div>}
            {n.edited_at && <div className="text-[10px] text-slate-400 mt-1" data-testid={`note-edited-${n.id}`}>Edited by {n.edited_by}, {stamp(n.edited_at)}</div>}
            {n.pinned_at && <div className="text-[10px] text-amber-700 mt-1" data-testid={`note-pinned-${n.id}`}>Pinned by {n.pinned_by}, {stamp(n.pinned_at)}</div>}
          </div>
          </div>
        ))}
      </div>
    </div>
  );
}
